import React from 'react';

import {
  GoogleSignInResult,
  loadGoogleClientId,
  loadGoogleScript,
  signInWithGoogleCredential,
} from '../utils/GoogleSignIn';

import './GoogleSignInButton.scss';

interface GoogleSignInButtonProps {
  /** Which words Google puts on its button. */
  mode: 'signin' | 'signup';
  onResult: (result: GoogleSignInResult) => void;
  onError: (message: string) => void;
}

/**
 * Google's own "Sign in with Google" button, above the email form with an "or" under it.
 * Shows nothing at all when the server has no Google client id.
 */
const GoogleSignInButton = ({ mode, onResult, onError }: GoogleSignInButtonProps) => {
  const [clientId, setClientId] = React.useState<string | null>(null);
  const [failed, setFailed] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const holder = React.useRef<HTMLDivElement>(null);

  // Google keeps the first callback it is given, so it calls through this to the latest.
  const handlers = React.useRef({ onResult, onError });
  handlers.current = { onResult, onError };

  React.useEffect(() => {
    let live = true;
    loadGoogleClientId().then(id => live && setClientId(id));
    return () => {
      live = false;
    };
  }, []);

  React.useEffect(() => {
    if (!clientId) return;
    let live = true;
    loadGoogleScript()
      .then(gsi => {
        if (!live || !holder.current) return;
        gsi.initialize({
          client_id: clientId,
          ux_mode: 'popup',
          callback: (response: { credential?: string }) => {
            if (!response || !response.credential) {
              handlers.current.onError('Google sign-in did not work. Please try again.');
              return;
            }
            setBusy(true);
            signInWithGoogleCredential(response.credential)
              .then(result => {
                setBusy(false);
                handlers.current.onResult(result);
              })
              .catch(error => {
                setBusy(false);
                handlers.current.onError(error.message);
              });
          },
        });
        // Google's button is 200 to 400 pixels wide; fill the form up to that.
        const width = Math.max(200, Math.min(400, Math.floor(holder.current.offsetWidth || 320)));
        gsi.renderButton(holder.current, {
          type: 'standard',
          theme: 'outline',
          size: 'large',
          shape: 'pill',
          text: mode === 'signup' ? 'signup_with' : 'signin_with',
          logo_alignment: 'center',
          width,
        });
      })
      .catch(() => live && setFailed(true));
    return () => {
      live = false;
    };
  }, [clientId, mode]);

  if (!clientId) {
    return null;
  }

  return (
    <div className="google-signin">
      {failed ? (
        <p className="google-signin-note">Google sign-in could not load. You can still use your email.</p>
      ) : (
        <div className="google-signin-button" ref={holder} />
      )}
      {busy && <p className="google-signin-note">Signing in…</p>}
      <div className="or-divider" role="separator">
        <span>or</span>
      </div>
    </div>
  );
};

export default GoogleSignInButton;
